import { FC, useContext, useState } from "react"
import { useParams } from "react-router-dom";

import { Typography, Tag } from "antd";
import { AnchorLinkItemProps } from "antd/es/anchor/Anchor";
import { DownloadOutlined } from "@ant-design/icons";
import { ActionButton } from '@hdi-seguros/web-components.action-button';
import { Card } from "@hdi-seguros/web-components.card";

import { Claim, DECISION_TYPE, Payment } from "../../../../../shared/interfaces";
import { UIContext } from "../../../../../shared/contexts";
import { COLORS } from "../../../../../config/themes/colors";
import { ErrorResults, SectionsSkeleton, Anchor, RowSize, ColSize } from "../../../../../shared/components";
import { Column, _renderColumns } from "../../utils/renderSections";
import { formatDate } from "../../../../../shared/utils";
import { PaymentsModal } from "../PaymentsModal";

import './index.css';
import { getClaimPDF } from "../../../../sinister/ClaimList/utils/claim-documents";
import { fixEncoding } from "../../../../../shared/services/insurancePolicy/utils";
interface ClaimsTabProps {
    claimsPerPolicy: Array<Claim>;
    showError?: boolean;
}

const MOCK_SECTIONS: Array<AnchorLinkItemProps> = [
    {
        key: 'siniestros',
        href: '#siniestros',
        title: 'Siniestros'
    }
];


export const ClaimsTab: FC<ClaimsTabProps> = ({ claimsPerPolicy, showError = false }) => {

    const { id } = useParams();

    const { isLoading } = useContext( UIContext );

    const [showPaymentsModal, setShowPaymentsModal] = useState<boolean>(false);
    const [paymentsSelected, setPaymentsSelected] = useState<Array<Payment>>([]);

    const handleClickShowPayments = (payments: Array<Payment>) => {
        setPaymentsSelected(payments);
        setShowPaymentsModal(true);
    }

    const generateCardInfo = ({ nroSiniestro, estado, asegurado, causa, juicio, fechaDenuncia, fechaSiniestro, pagos, denunciaPdf, bienSiniestrado, documentoPredenuncia, rama }: Claim): Array<Column> => ([
        {
            label: 'Número',
            value: nroSiniestro,
            columnSize: { xs: { span: 12 }, sm: { span: 6 }, md: { span: 4 } },
        },
        {
            label: 'Estado',
            value: estado ? <Tag color="geekblue" >{ estado }</Tag> : '-',
            columnSize: { xs: { span: 12 }, sm: { span: 6 }, md: { span: 4 } },
        },
        {
            label: 'Asegurado',
            value: asegurado,
            columnSize: { xs: { span: 12 }, sm: { span: 6 }, md: { span: 6 } },
        },
        {
            label: 'Causa',
            value: fixEncoding(causa),
            columnSize: { xs: { span: 12 }, sm: { span: 6 }, md: { span: 5 } },
        },
        {
            label: 'En Juicio',
            value: juicio === 'S' ? 'Si' : 'No',
            columnSize: { xs: { span: 12 }, sm: { span: 6 }, md: { span: 5 } },
        },
        {
            label: 'Fecha Denuncia',
            value: formatDate(fechaDenuncia),
            columnSize: { xs: { span: 12 }, sm: { span: 6 }, md: { span: 4 } },
        },
        {
            label: 'Fecha Siniestro',
            value: formatDate(fechaSiniestro),
            columnSize: { xs: { span: 12 }, sm: { span: 6 }, md: { span: 4 } },
        },
        {
            label: 'Estado del pago',
            value: pagos?.length ? <ActionButton label="Ver Pagos" onClick={() => handleClickShowPayments(pagos)} type="text" /> : 'No hay pagos' ,
            columnSize: { xs: { span: 12 }, sm: { span: 6 }, md: { span: 6 } },
        },
        {
            label: 'Bien Siniestrado',
            value: bienSiniestrado,
            columnSize: { xs: { span: 12 }, sm: { span: 6 }, md: { span: 5 } },
        },
        {
            label: 'Denuncia',
            value: denunciaPdf === DECISION_TYPE.YES ? <ActionButton className="btn-accent-outline-barbuss" type="secondary" label="Descargar" onClick={() => getClaimPDF(documentoPredenuncia, rama, nroSiniestro)} icon={<DownloadOutlined />}  /> : '-',
            columnSize: { xs: { span: 12 }, sm: { span: 6 }, md: { span: 5 } },
        }
    ]);

    return (
        <div style={{ width: '100%' }}>
            <RowSize rowProps={{gutter:[0 , 0]}}>
                <ColSize size={ 85 } colProps={{ xs:{span: 21}, sm:{span: 21}}}> 
                    {
                        showError
                            ? <></>
                            : (
                                <Typography.Title level={ 4 } style={{ margin: 0, marginBottom: 10, marginTop: 20, color: COLORS.principal[100] }}>
                                    Siniestros
                                </Typography.Title>
                            )
                    }

                    {
                        isLoading
                            ?   <SectionsSkeleton />
                            :   showError
                                ? <ErrorResults title="Hubo un error al intentar cargar el listado de siniestros" />
                                :  claimsPerPolicy?.length
                                    ? (
                                        <RowSize rowProps={{gutter:[0 , 0]}}>
                                            <ColSize size={ 95 } colProps={{ xs:{span: 23}, sm:{span: 23}}}>
                                                {
                                                    claimsPerPolicy.map(( claim, i ) => (
                                                        <Card
                                                            key={ i }
                                                            style={{ marginBottom: 20 }}
                                                        >
                                                            <RowSize rowProps={{gutter:[0 , 15]}} >
                                                                { _renderColumns(generateCardInfo(claim)) }
                                                            </RowSize>
                                                        </Card>
                                                    ))
                                                }
                                            </ColSize>
                                        </RowSize>
                                    )
                                    : (
                                        <Typography.Text>
                                            No hay siniestros para la póliza { id }
                                        </Typography.Text>
                                    )
                    }
                </ColSize>

                {
                    !isLoading && !showError && (
                        <>
                            <ColSize size={ 9 } colProps={{id:"claims-tab-anchor-container", xs:{span: 3}, sm:{span: 3}}} >
                                <Anchor
                                    items={ MOCK_SECTIONS }
                                    containerId="claims-tab-anchor-container"
                                />
                            </ColSize>

                            <PaymentsModal
                                title="Estado del pago"
                                open={ showPaymentsModal }
                                onCancel={() => setShowPaymentsModal(false)}
                                payments={ paymentsSelected }
                            />
                        </>
                    )
                }
            </RowSize>
        </div>
    )
}
